import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, BookOpen, BarChart3, Users, Settings as SettingsIcon, LogOut, ShieldCheck, GraduationCap, Award, Sparkles, Heart, ShoppingCart, Tag, CreditCard, Building2 } from "lucide-react";
import { LOGO_URL } from "../constants";
import { useAuth } from "../contexts/AuthContext";
import useMobile from "../hooks/useMobile";

const navItems = [
    { to: "/dashboard", icon: Home, label: "Dashboard" },
    { to: "/my-courses", icon: GraduationCap, label: "My Courses" },
    { to: "/catalog", icon: BookOpen, label: "Courses" },
    { to: "/wishlist", icon: Heart, label: "Wishlist" },
    { to: "/cart", icon: ShoppingCart, label: "Cart" },
    { to: "/progress", icon: BarChart3, label: "Progress" },
    { to: "/certificates", icon: Award, label: "Certificates" },
    { to: "/settings", icon: SettingsIcon, label: "Settings" },
];

const managerNavItem = { to: "/my-team", icon: Users, label: "My Team" };

const adminNavItems = [
    { to: "/admin", icon: Home, label: "Overview" },
    { to: "/admin/analytics", icon: BarChart3, label: "Analytics" },
    { to: "/admin/customers", icon: Users, label: "Customers" },
    { to: "/admin/organisations", icon: Building2, label: "Organisations" },
    { to: "/admin/courses", icon: BookOpen, label: "Courses" },
    { to: "/admin/enrollments", icon: GraduationCap, label: "Enrollments" },
    { to: "/admin/certificates", icon: Award, label: "Certificates" },
    { to: "/admin/leads", icon: Sparkles, label: "Leads" },
    { to: "/admin/discounts", icon: Tag, label: "Discounts" },
    { to: "/admin/refunds", icon: CreditCard, label: "Payments" },
];

export default function Sidebar({ navOpen, onNavClose }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const isMobile = useMobile();

    const isAdmin     = location.pathname.startsWith("/admin");
    const isAdminUser = user?.role === "admin";
    const isManager   = user?.role === "manager";
    const baseItems   = isAdmin ? adminNavItems : navItems;
    const items       = isAdmin
        ? baseItems
        : isManager
            ? [...baseItems, managerNavItem]
            : baseItems;

    const handleNavClick = () => {
        if (isMobile) onNavClose?.();
    };

    const handleLogout = async () => {
        await logout();
        navigate("/");
        onNavClose?.();
    };

    if (isAdmin && !isAdminUser) {
        navigate("/dashboard");
        return null;
    }

    const sidebarStyle = isMobile ? {
        position: "fixed",
        top: 0, left: 0, bottom: 0,
        zIndex: 60,
        width: 260,
        flexShrink: 0,
        transform: navOpen ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.28s ease",
        display: "flex",
        flexDirection: "column",
        background: "#0A0B0E",
        borderRight: "1px solid rgba(255,255,255,0.07)",
        padding: "24px 16px",
        overflowY: "auto",
    } : {
        display: "flex",
        flexDirection: "column",
        width: 240,
        flexShrink: 0,
        height: "100vh",
        position: "sticky",
        top: 0,
        background: "#0A0B0E",
        borderRight: "1px solid rgba(255,255,255,0.07)",
        padding: "24px 16px",
    };

    return (
        <>
            {/* Backdrop — mobile only */}
            {isMobile && navOpen && (
                <div
                    onClick={onNavClose}
                    style={{
                        position: "fixed", inset: 0,
                        background: "rgba(0,0,0,0.65)",
                        zIndex: 59,
                        backdropFilter: "blur(2px)",
                    }}
                />
            )}

            <aside data-testid="app-sidebar" style={sidebarStyle}>
                {/* Logo */}
                <NavLink
                    to="/"
                    onClick={handleNavClick}
                    data-testid="brand-logo"
                    style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, padding: "0 8px" }}
                >
                    <img src={LOGO_URL} alt="Blue Mountain Group" style={{ height: 44, width: "auto", objectFit: "contain" }} />
                </NavLink>

                <NavLink
                    to="/"
                    onClick={handleNavClick}
                    data-testid="sidebar-home"
                    style={{
                        marginBottom: 24, fontSize: 10, color: "#6b7280",
                        textDecoration: "none", display: "inline-flex",
                        alignItems: "center", gap: 4, padding: "0 8px",
                        fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em",
                        transition: "color 0.2s"
                    }}
                >
                    ← Back to marketing site
                </NavLink>

                {isAdmin && (
                    <div style={{
                        marginBottom: 16, padding: "6px 12px",
                        borderRadius: 99, background: "rgba(124,92,255,0.12)",
                        border: "1px solid rgba(124,92,255,0.3)",
                        color: "#7C5CFF", fontSize: 11, fontWeight: 700,
                        display: "inline-flex", alignItems: "center", gap: 6,
                        alignSelf: "flex-start"
                    }}>
                        <ShieldCheck size={12} /> Admin Mode
                    </div>
                )}

                {/* Nav */}
                <nav style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
                    {items.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/admin"}
                                onClick={handleNavClick}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                style={({ isActive }) => ({
                                    display: "flex", alignItems: "center",
                                    justifyContent: "space-between", gap: 12,
                                    padding: "10px 12px", borderRadius: 12,
                                    textDecoration: "none",
                                    color: isActive ? "#fff" : "#6b7280",
                                    background: isActive ? "rgba(255,255,255,0.07)" : "transparent",
                                    border: isActive ? "1px solid rgba(255,255,255,0.08)" : "1px solid transparent",
                                    fontWeight: isActive ? 600 : 400,
                                    fontSize: 13, transition: "all 0.15s"
                                })}
                            >
                                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <Icon size={16} />
                                    {item.label}
                                </span>
                                {item.badge && (
                                    <span style={{
                                        fontSize: 10, fontWeight: 800,
                                        padding: "2px 7px", borderRadius: 99,
                                        background: "#7C5CFF", color: "#fff"
                                    }}>{item.badge}</span>
                                )}
                            </NavLink>
                        );
                    })}
                </nav>

                {/* Logout / Sign in */}
                {user ? (
                    <button
                        onClick={handleLogout}
                        data-testid="logout-btn"
                        style={{
                            marginTop: 8, display: "flex", alignItems: "center", gap: 10,
                            padding: "10px 12px", color: "#6b7280", fontSize: 13,
                            background: "transparent", border: "none",
                            cursor: "pointer", transition: "color 0.2s"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = "#ef4444"}
                        onMouseLeave={(e) => e.currentTarget.style.color = "#6b7280"}
                    >
                        <LogOut size={15} /> Log out
                    </button>
                ) : (
                    <NavLink
                        to="/login"
                        onClick={handleNavClick}
                        data-testid="login-link"
                        style={{
                            marginTop: 8, display: "flex", alignItems: "center",
                            justifyContent: "center", gap: 8,
                            padding: "12px 16px", borderRadius: 12,
                            background: "#CFFF50", color: "#0A0B0E",
                            fontWeight: 800, fontSize: 13, textDecoration: "none"
                        }}
                    >
                        Sign in
                    </NavLink>
                )}
            </aside>
        </>
    );
}
